
<script context="module">

import { 
  VmPlayer 
} from '@vime/core';

import { define } from '../lib';
  

define('vm-player', VmPlayer);
  
</script>

<script>
import { createEventDispatcher, onMount } from 'svelte';
import { setProp } from '../lib';

let __ref;
let __mounted = false;

const dispatch = createEventDispatcher();

let className;

export let style;
export { className as class };

export let logger = undefined;
export let theme = undefined;
export let icons = 'vime';
export let paused = true;
export let playing = false;
export let duration = -1;
export let mediaTitle = undefined;
export let currentProvider = undefined;
export let currentSrc = undefined;
export let currentPoster = undefined;
export let currentTime = 0;
export let autoplay = false;
export let ready = false;
export let playbackReady = false;
export let loop = false;
export let muted = false;
export let buffered = 0;
export let playbackRate = 1;
export let playbackRates = [1];
export let playbackQuality = undefined;
export let playbackQualities = [];
export let seeking = false;
export let debug = false;
export let playbackStarted = false;
export let playbackEnded = false;
export let buffering = false;
export let controls = false;
export let isControlsActive = false;
export let isSettingsActive = false;
export let volume = 50;
export let isFullscreenActive = false;
export let aspectRatio = '16:9';
export let viewType = undefined;
export let isAudioView = false;
export let isVideoView = false;
export let mediaType = undefined;
export let isAudio = false;
export let isVideo = false;
export let isLive = false;
export let isMobile = false;
export let isTouch = false;
export let isPiPActive = false;
export let textTracks = [];
export let currentTextTrack = -1;
export let isTextTrackVisible = true;
export let shouldRenderNativeTextTracks = true;
export let audioTracks = [];
export let currentAudioTrack = -1;
export let autopause = true;
export let playsinline = false;
export let language = 'en';
export let translations = undefined;
export let languages = ['en'];
export let i18n = undefined;
export const getProvider = (...args) => __ref.getProvider(...args);
export const getAdapter = (...args) => __ref.getAdapter(...args);
export const play = (...args) => __ref.play(...args);
export const pause = (...args) => __ref.pause(...args);
export const canPlay = (...args) => __ref.canPlay(...args);
export const canAutoplay = (...args) => __ref.canAutoplay(...args);
export const canMutedAutoplay = (...args) => __ref.canMutedAutoplay(...args);
export const canSetPlaybackRate = (...args) => __ref.canSetPlaybackRate(...args);
export const canSetPlaybackQuality = (...args) => __ref.canSetPlaybackQuality(...args);
export const canSetFullscreen = (...args) => __ref.canSetFullscreen(...args);
export const enterFullscreen = (...args) => __ref.enterFullscreen(...args);
export const exitFullscreen = (...args) => __ref.exitFullscreen(...args);
export const canSetPiP = (...args) => __ref.canSetPiP(...args);
export const enterPiP = (...args) => __ref.enterPiP(...args);
export const exitPiP = (...args) => __ref.exitPiP(...args);
export const canSetAudioTrack = (...args) => __ref.canSetAudioTrack(...args);
export const setCurrentAudioTrack = (...args) => __ref.setCurrentAudioTrack(...args);
export const canSetTextTrack = (...args) => __ref.canSetTextTrack(...args);
export const setCurrentTextTrack = (...args) => __ref.setCurrentTextTrack(...args);
export const canSetTextTrackVisibility = (...args) => __ref.canSetTextTrackVisibility(...args);
export const setTextTrackVisibility = (...args) => __ref.setTextTrackVisibility(...args);
export const extendLanguage = (...args) => __ref.extendLanguage(...args);
export const getContainer = (...args) => __ref.getContainer(...args);
export const callAdapter = (...args) => __ref.callAdapter(...args);
export const ref = () => __ref;
export const getWebComponent = () => __ref;

onMount(() => { 
  logger = (logger === void 0) ? __ref['logger'] : logger;
  translations = (translations === void 0) ? __ref['translations'] : translations;
  i18n = (i18n === void 0) ? __ref['i18n'] : i18n;

  __mounted = true; 
});

$: if (__mounted) setProp(__ref, 'logger', logger);
$: if (__mounted) setProp(__ref, 'playbackRates', playbackRates);
$: if (__mounted) setProp(__ref, 'playbackQualities', playbackQualities);
$: if (__mounted) setProp(__ref, 'textTracks', textTracks);
$: if (__mounted) setProp(__ref, 'audioTracks', audioTracks);
$: if (__mounted) setProp(__ref, 'translations', translations);
$: if (__mounted) setProp(__ref, 'languages', languages);
$: if (__mounted) setProp(__ref, 'i18n', i18n);

const onEvent = (e) => {
  e.stopPropagation();
  dispatch(e.type, e.detail);
};
</script>

<vm-player 
  class={className}
  style={style}
  theme={theme}
  icons={icons}
  paused={paused}
  playing={playing}
  duration={duration}
  media-title={mediaTitle}
  current-provider={currentProvider}
  current-src={currentSrc}
  current-poster={currentPoster}
  current-time={currentTime}
  autoplay={autoplay}
  ready={ready}
  playback-ready={playbackReady}
  loop={loop}
  muted={muted}
  buffered={buffered}
  playback-rate={playbackRate}
  playback-quality={playbackQuality}
  seeking={seeking}
  debug={debug}
  playback-started={playbackStarted}
  playback-ended={playbackEnded}
  buffering={buffering}
  controls={controls}
  is-controls-active={isControlsActive}
  is-settings-active={isSettingsActive}
  volume={volume}
  is-fullscreen-active={isFullscreenActive}
  aspect-ratio={aspectRatio}
  view-type={viewType}
  is-audio-view={isAudioView}
  is-video-view={isVideoView}
  media-type={mediaType}
  is-audio={isAudio}
  is-video={isVideo}
  is-live={isLive}
  is-mobile={isMobile}
  is-touch={isTouch}
  is-pi-p-active={isPiPActive}
  current-text-track={currentTextTrack}
  is-text-track-visible={isTextTrackVisible}
  should-render-native-text-tracks={shouldRenderNativeTextTracks}
  current-audio-track={currentAudioTrack}
  autopause={autopause}
  playsinline={playsinline}
  language={language}
  on:vmThemeChange={onEvent}
  on:vmPausedChange={onEvent}
  on:vmPlay={onEvent}
  on:vmPlayingChange={onEvent}
  on:vmSeekingChange={onEvent}
  on:vmSeeked={onEvent}
  on:vmBufferingChange={onEvent}
  on:vmDurationChange={onEvent}
  on:vmCurrentTimeChange={onEvent}
  on:vmReady={onEvent}
  on:vmPlaybackReady={onEvent}
  on:vmPlaybackStarted={onEvent}
  on:vmPlaybackEnded={onEvent}
  on:vmBufferedChange={onEvent}
  on:vmError={onEvent}
  on:vmLoadStart={onEvent}
  on:vmCurrentProviderChange={onEvent}
  on:vmCurrentSrcChange={onEvent}
  on:vmCurrentPosterChange={onEvent}
  on:vmMediaTitleChange={onEvent}
  on:vmControlsChange={onEvent}
  on:vmPlaybackRateChange={onEvent}
  on:vmPlaybackRatesChange={onEvent}
  on:vmPlaybackQualityChange={onEvent}
  on:vmPlaybackQualitiesChange={onEvent}
  on:vmMutedChange={onEvent}
  on:vmVolumeChange={onEvent}
  on:vmViewTypeChange={onEvent}
  on:vmMediaTypeChange={onEvent}
  on:vmLiveChange={onEvent}
  on:vmTouchChange={onEvent}
  on:vmLanguageChange={onEvent}
  on:vmI18nChange={onEvent}
  on:vmTranslationsChange={onEvent}
  on:vmLanguagesChange={onEvent}
  on:vmFullscreenChange={onEvent}
  on:vmPiPChange={onEvent}
  on:vmTextTracksChange={onEvent}
  on:vmCurrentTextTrackChange={onEvent}
  on:vmTextTrackVisibleChange={onEvent}
  on:vmAudioTracksChange={onEvent}
  on:vmCurrentAudioTrackChange={onEvent}
  bind:this={__ref}
>
  <slot></slot>
</vm-player>
  